// Apolo IDE — single pull-request card.
//
// Epic 15: when the list comes from GitHub (source='github'), each PR carries
// head_ref (branch), additions/deletions (diff) and ci (pass|fail|pending|none).
// These render only when present, so the mock list (which omits them) degrades
// cleanly to the original compact card.

import { CheckCircle2, CircleDot, GitPullRequest, GitBranch } from 'lucide-react';

const STATUS_STYLE = {
  merged: { icon: CheckCircle2, color: 'text-apolo-accent', label: 'Mergeado' },
  in_review: { icon: CircleDot, color: 'text-apolo-warning', label: 'Em revisão' },
  draft: { icon: GitPullRequest, color: 'text-apolo-textTertiary', label: 'Draft' },
  closed: { icon: GitPullRequest, color: 'text-apolo-textTertiary', label: 'Closed' },
};

const SIZE_LABEL = {
  small: 'pequeno',
  medium: 'médio',
  large: 'grande',
};

// CI badge presentation. `none` renders nothing (no checks configured).
const CI_STYLE = {
  pass: { dot: 'bg-apolo-success', text: 'text-apolo-success', label: 'CI', title: 'CI passou' },
  fail: { dot: 'bg-apolo-error', text: 'text-apolo-error', label: 'CI', title: 'CI falhou' },
  pending: { dot: 'bg-apolo-warning', text: 'text-apolo-warning', label: 'CI', title: 'CI em andamento' },
};

function CiBadge({ ci }) {
  const style = CI_STYLE[ci];
  if (!style) return null; // 'none' / undefined → nothing
  return (
    <span
      className={`inline-flex items-center gap-1 ${style.text}`}
      title={style.title}
      aria-label={style.title}
    >
      {ci === 'pending' ? (
        <span
          aria-hidden="true"
          className={`block h-2 w-2 animate-spin rounded-full border border-current border-t-transparent`}
        />
      ) : (
        <span aria-hidden="true" className={`block h-2 w-2 rounded-full ${style.dot}`} />
      )}
      {style.label}
    </span>
  );
}

export default function PullRequestCard({ pr }) {
  const style = STATUS_STYLE[pr.status] || STATUS_STYLE.draft;
  const Icon = style.icon;
  const date = new Date(pr.updated_at);

  const hasDiff = typeof pr.additions === 'number' && typeof pr.deletions === 'number';

  return (
    <article className="flex items-start gap-3 rounded-lg border border-apolo-borderSubtle bg-apolo-surface1 p-3 transition hover:border-apolo-accent/60">
      <Icon size={16} className={`mt-0.5 shrink-0 ${style.color}`} aria-hidden="true" />
      <div className="flex flex-1 flex-col gap-1">
        {/* (Pedro 2026-06-25, sprint cursor-strip-4 R4-4) — o # do GitHub
           vai à ESQUERDA do slug do repo. Texto destacado (não um <a> próprio:
           o card inteiro já costuma estar embrulhado num link pro PR; aninhar
           âncoras seria HTML inválido). */}
        <p className="flex items-center gap-1.5 text-xs text-apolo-textTertiary">
          <span
            className="font-mono font-semibold text-apolo-textSecondary"
            title={`PR #${pr.pr_number}`}
          >
            #{pr.pr_number}
          </span>
          <span aria-hidden="true">·</span>
          <span className="truncate">{pr.repo}</span>
        </p>
        <h3 className="line-clamp-2 text-sm font-medium text-apolo-textPrimary">{pr.title}</h3>

        {/* branch + diff row — only when GitHub enrichment provided them */}
        {(pr.head_ref || hasDiff) && (
          <div className="flex items-center gap-3 text-xs text-apolo-textTertiary">
            {pr.head_ref && (
              <span className="inline-flex items-center gap-1 font-mono" title={`branch: ${pr.head_ref}`}>
                <GitBranch size={12} aria-hidden="true" />
                <span className="max-w-[160px] truncate">{pr.head_ref}</span>
              </span>
            )}
            {hasDiff && (
              <span className="font-mono" aria-label={`${pr.additions} adições, ${pr.deletions} remoções`}>
                <span className="text-apolo-success">+{pr.additions}</span>{' '}
                <span className="text-apolo-error">−{pr.deletions}</span>
              </span>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 text-xs text-apolo-textTertiary">
          <span className={style.color}>{style.label}</span>
          {pr.ci && pr.ci !== 'none' && (
            <>
              <span aria-hidden="true">·</span>
              <CiBadge ci={pr.ci} />
            </>
          )}
          {pr.size && pr.size !== 'unknown' && (
            <>
              <span aria-hidden="true">·</span>
              <span>tamanho: {SIZE_LABEL[pr.size] || pr.size}</span>
            </>
          )}
          <span aria-hidden="true">·</span>
          <time dateTime={pr.updated_at}>{date.toLocaleDateString('pt-BR')}</time>
        </div>
      </div>
    </article>
  );
}
